import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Form, FormSpy } from 'react-final-form';
import { ComposableMap, ZoomableGroup, Geographies, Geography, Marker } from 'react-simple-maps';
import { feature } from 'topojson-client'; // Dependency of react-simple-maps.
import { geoCentroid } from 'd3-geo';

import Link from '../../../01_atoms/Link';
import Spinner from '../../../01_atoms/Spinner';
import Button from '../../../01_atoms/Button';
import { SelectFieldStandalone } from '../../../01_atoms/FormElements';
import withIE, { IEContext } from '../../../05_hoc/withIE';
import ZoomInSvg from '../../../../public/static/icons/map-zoom-in.svg';
import ZoomOutSvg from '../../../../public/static/icons/map-zoom-out.svg';
import CloseSvg from '../../../../public/static/icons/close-green.svg';
import FormattedText from '../../../01_atoms/FormattedText';

import styles from './index.module.scss';

class BBCountryMap extends React.Component {
  /**
   * Define min zooming value for the worldwide map.
   */
  minMapZoom = 2;

  /**
   * Define max zooming value for the worldwide map.
   */
  maxMapZoom = 7;

  /**
   * Display style for the whole map.
   */
  mapStyle = {
    margin: '0 auto',
    display: 'block',
    position: 'relative',
    zIndex: 3,
  };

  /**
   * Style for the country which is in the list of where CW work.
   */
  enabledCountryStyle = {
    default: {
      fill: '#d3d3d6',
      stroke: '#bebebe',
      strokeWidth: 0.25,
      outline: 'none',
    },
    hover: {
      fill: '#1bb7ad',
      stroke: '#1bb7ad',
      cursor: 'pointer',
      strokeWidth: 0.25,
      outline: 'none',
    },
    pressed: {
      fill: '#1bb7ad',
      stroke: '#1bb7ad',
      strokeWidth: 0.25,
      outline: 'none',
    },
  };

  /**
   * Style for the country which is hovered or clicked (active).
   */
  activeCountryStyle = {
    default: {
      fill: '#1bb7ad',
      stroke: '#1bb7ad',
      strokeWidth: 0.25,
      outline: 'none',
    },
    hover: {
      fill: '#1bb7ad',
      stroke: '#1bb7ad',
      cursor: 'pointer',
      strokeWidth: 0.25,
      outline: 'none',
    },
    pressed: {
      fill: '#1bb7ad',
      stroke: '#1bb7ad',
      strokeWidth: 0.25,
      outline: 'none',
    },
  };

  /**
   * Style for country which is in not the list of where CW work.
   */
  inactiveCountryStyle = {
    default: {
      fill: '#f4f4f5',
      stroke: '#bebebe',
      strokeWidth: 0.25,
      outline: 'none',
    },
    hover: {
      fill: '#f4f4f5',
      stroke: '#bebebe',
      strokeWidth: 0.25,
      outline: 'none',
    },
    pressed: {
      fill: '#f4f4f5',
      stroke: '#bebebe',
      strokeWidth: 0.25,
      outline: 'none',
    },
  };

  /**
   * Style for marker (pin) on the map.
   */
  markerStyle = {
    default: {
      outline: 'none',
    },
    hover: {
      cursor: 'pointer',
      outline: 'none',
    },
    pressed: {
      outline: 'none',
    },
  };

  /**
   * Default keycodes
   */

  keyCodes = {
    enter: 13,
    space: 32,
    esc: 27,
  };

  constructor(props) {
    super(props);

    this.state = {
      // Default lat / lon of the map center. Will be changed if user
      // uses select field to choose a country.
      mapCenter: [28, 13],
      // Default map zooming. Will be changed if user clicks + or -
      mapZoom: this.minMapZoom + 0.65,
      // Will be adjusted to the screen width on componentDidMount().
      mapWidth: 980,
      // Large geo data. Will be loaded in componentDidMount().
      mapData: [],
      // List of country ISO codes where CW work. It's here for performance
      // reasons.
      countryCodes: props.countries.map((country) => country.code),
      // ISO code of hovered or clicked country.
      activeCountryCode: '',
      // Country data selected for display as a card.
      cardCountry: null,
    };

    this.ref = React.createRef();
    this.onMapZoomIn = this.onMapZoomIn.bind(this);
    this.onMapZoomOut = this.onMapZoomOut.bind(this);
    this.onCountryMouseEnter = this.onCountryMouseEnter.bind(this);
    this.onCountryMouseLeave = this.onCountryMouseLeave.bind(this);
    this.onCountryClick = this.onCountryClick.bind(this);
    this.onCountryCardClose = this.onCountryCardClose.bind(this);
    this.onCountrySelect = this.onCountrySelect.bind(this);
    this.onPageScrollOrResize = this.onPageScrollOrResize.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyDownOnCloseIcon = this.onKeyDownOnCloseIcon.bind(this);
    this.onMapZoomInKeyPress = this.onMapZoomInKeyPress.bind(this);
    this.onMapZoomOutKeyPress = this.onMapZoomOutKeyPress.bind(this);
    this.tooltipCountryName = React.createRef(null);
    this.tooltipCountryCard = React.createRef(null);
  }

  componentDidMount() {
    // The map is rendered on desktop only.
    if (!window.matchMedia || !window.matchMedia('(min-width: 992px)').matches) {
      return;
    }
    // Load geo data for all countries (world map).
    import('../../../../utils/world-50m.json').then((json) => {
      const world = json && json.default ? json.default : json;
      if (world && world.arcs) {
        const mapData = feature(world, world.objects[Object.keys(world.objects)[0]]).features;
        this.setState({
          mapData,
          mapWidth: Math.ceil(this.ref.current.getBoundingClientRect().width),
        });
      }
    });

    document.addEventListener('scroll', this.onPageScrollOrResize);
    document.addEventListener('resize', this.onPageScrollOrResize);
    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.onPageScrollOrResize);
    document.removeEventListener('resize', this.onPageScrollOrResize);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  /**
   * Handles click on + button on the map.
   */
  onMapZoomIn() {
    this.setState((state) => ({
      mapZoom: state.mapZoom >= this.maxMapZoom ? this.maxMapZoom : state.mapZoom + 1,
    }));

    // Close card with country if it existed before.
    this.onPageScrollOrResize();
  }

  /**
   * Handles click on - button on the map.
   */
  onMapZoomOut() {
    this.setState((state) => ({
      mapZoom: state.mapZoom <= this.minMapZoom ? this.minMapZoom : state.mapZoom - 1,
    }));

    // Close card with country if it existed before.
    this.onPageScrollOrResize();
  }

  /**
   * Handled keyboard click on ZoomIn element.
   */
  onMapZoomInKeyPress(e) {
    if (e.charCode == this.keyCodes.enter || e.charCode == this.keyCodes.space) {
      this.onMapZoomIn();
      e.preventDefault();
    }
  }

  /**
   * Handles keyboard click on ZoomOut element.
   */
  onMapZoomOutKeyPress(e) {
    if (e.charCode == this.keyCodes.enter || e.charCode == this.keyCodes.space) {
      this.onMapZoomOut();
      e.preventDefault();
    }
  }

  /**
   * Handles hover (mouse enter) behavior on an individual country.
   */
  onCountryMouseEnter(countryCode) {
    const { cardCountry } = this.state;

    // If any country is opened in a card, then don't show labels for countries.
    if (!cardCountry && this.state.countryCodes.includes(countryCode)) {
      this.setState({ activeCountryCode: countryCode });

      const marker = document.querySelectorAll(`a[data-marker-id="${countryCode}"]`);
      const coords = marker[0].getBoundingClientRect();

      this.tooltipCountryName.current.open({
        anchorSelect: '#country-name',
        position: { x: coords.x + this.state.mapZoom * 3.5, y: coords.y },
      });
    }
  }

  /**
   * Handles end of hover (mouse leave) behavior on an individual country.
   */
  onCountryMouseLeave(countryCode) {
    const { cardCountry } = this.state;

    // Don't change the active country if the country card is open.
    if (!cardCountry) {
      this.setState({ activeCountryCode: '' });

      this.tooltipCountryName.current.close();
    }
  }

  /**
   * Handles click on the country on the map.
   */
  onCountryClick(countryCode) {
    // Set the country data for the card & open card in popup.
    this.setState((state, props) => ({
      cardCountry: props.countries.find((country) => country.code === countryCode),
      activeCountryCode: countryCode,
    }));

    const marker = document.querySelectorAll(`a[data-area-id="${countryCode}"]`);

    if (marker.length) {
      const coords = marker[0].getBoundingClientRect();

      this.tooltipCountryCard.current.open({
        anchorSelect: '#country-card',
        position: { x: 0, y: 0 },
      });

      // The newest version of "react-tooltip" library can't calculate correctly
      // the right position for the block from right side. Here it will be calculated
      // manually.
      setTimeout(() => {
        const elem = document.getElementById('country-card');

        if (elem) {
          elem.style.left = coords.x + coords.width + 'px';
          elem.style.top = window.pageYOffset + coords.y - 200 + coords.height / 2 + 'px';
          elem.style.opacity = 1;
        }
      }, 50);
    }
  }

  /**
   * Handles close behavior of card.
   */
  onCountryCardClose() {
    this.setState({ cardCountry: null, activeCountryCode: '' });
  }

  /**
   * Handles selection of country name in the select field.
   */
  onCountrySelect({ values }) {
    const { mapData } = this.state;

    if (values.country) {
      // Grab country ISO code selected by the user.
      const countryCode = values.country.value;

      // Change the map center to the selected country center.
      const country = mapData.find((geo) => geo.id === countryCode);
      this.setState({ mapCenter: geoCentroid(country) });

      // When map center is set, just wait 100 ms to get the updated position on the
      // map and carry on usual click handling.
      setTimeout(() => this.onCountryClick(countryCode), 100);
    }
  }

  /**
   * Close country card on ESC press.
   */
  onKeyDown(event) {
    const { cardCountry } = this.state;

    if (cardCountry && event.keyCode === this.keyCodes.esc) {
      this.onCountryCardClose();
    }
  }

  /**
   * Close country card on icon press.
   */
  onKeyDownOnCloseIcon(event) {
    if (event.keyCode === this.keyCodes.enter || event.keyCode === this.keyCodes.space) {
      this.onCountryCardClose();
      // For preventing scroll on Space press.
      event.preventDefault();
    }
  }

  /**
   * Close country card on page scroll or resize.
   */
  onPageScrollOrResize() {
    const { cardCountry } = this.state;
    if (cardCountry) {
      this.onCountryCardClose();
    }
  }

  render() {
    const { countries, height, projectionConfig } = this.props;
    const { mapWidth, mapZoom, mapData, mapCenter, countryCodes, activeCountryCode, cardCountry } =
      this.state;

    const ZoomControls = withIE(() => (
      <IEContext.Consumer>
        {({ isIE }) => (
          <div
            className={`${styles['bb-country-map__controls-zooming']} ${isIE ? styles['bb-country-map__controls-zooming--ie11-compat'] : ''}`}
          >
            <ZoomInSvg
              tabIndex="0"
              aria-label="Zoom in"
              onClick={this.onMapZoomIn}
              onKeyPress={(e) => this.onMapZoomInKeyPress(e)}
              className={mapZoom === this.maxMapZoom ? 'disabled' : 'active'}
            />
            <ZoomOutSvg
              tabIndex="0"
              aria-label="Zoom out"
              onClick={this.onMapZoomOut}
              onKeyPress={(e) => this.onMapZoomOutKeyPress(e)}
              className={mapZoom === this.minMapZoom ? 'disabled' : 'active'}
            />
          </div>
        )}
      </IEContext.Consumer>
    ));

    return (
      <div className={`bb ${styles['bb-country-map']}`} ref={this.ref}>
        {/* Tooltip to display a country card on click */}
        <ReactTooltip
          id="country-card"
          ref={this.tooltipCountryCard}
          imperativeModeOnly={true}
          className="react-tooltip type-info place-right"
          content={
            cardCountry && (
              <div tabIndex="0" aria-label="Country card tooltip" className={'react-tooltip-body'}>
                <CloseSvg
                  tabIndex="0"
                  className="close-btn"
                  aria-label="Close tooltip"
                  onKeyDown={this.onKeyDownOnCloseIcon}
                  onClick={this.onCountryCardClose}
                />

                {cardCountry.image && cardCountry.image.url && (
                  <img
                    src={cardCountry.image.url}
                    alt={cardCountry.image.alt}
                    width={350}
                    height={150}
                  />
                )}

                <div className="content">
                  {cardCountry.name && <h2 className="title">{cardCountry.name}</h2>}

                  {cardCountry.description && (
                    <FormattedText className="description" text={cardCountry.description} />
                  )}

                  {cardCountry.nextLink && cardCountry.name && (
                    <Link {...cardCountry.nextLink}>
                      <Button
                        type="secondary"
                        className="bb-country-map--button"
                        isOutlined
                        tag="a"
                        href={cardCountry.nextLink.url}
                      >
                        {cardCountry.linkLabel
                          ? cardCountry.linkLabel
                          : `Explore ${cardCountry.name}`}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )
          }
        />
        {/* Tooltip for hover on country area */}
        <ReactTooltip
          id="country-name"
          className="react-tooltip type-light"
          ref={this.tooltipCountryName}
          imperativeModeOnly={true}
          variant="light"
          content={
            activeCountryCode &&
            !cardCountry && (
              <span>{countries.find((country) => country.code === activeCountryCode)?.name}</span>
            )
          }
        />
        <div className={styles['bb-country-map__map']}>
          {/* Show nice spinner while map is loading */}
          {mapData.length === 0 && <Spinner />}

          {mapData.length > 0 && (
            <>
              {/* Select field + Zoom controls */}
              <div className={styles['bb-country-map__controls']}>
                <div className="container">
                  <Form
                    onSubmit={() => {}}
                    render={({ handleSubmit }) => (
                      <form onSubmit={handleSubmit}>
                        <FormSpy subscription={{ values: true }} onChange={this.onCountrySelect} />
                        <SelectFieldStandalone
                          label="Find a country"
                          options={countries
                            .map((country) => ({ value: country.code, label: country.name }))
                            .sort((a, b) => {
                              if (a.label < b.label) return -1;
                              if (a.label > b.label) return 1;
                              return 0;
                            })}
                          name="country"
                        />
                      </form>
                    )}
                  />

                  <ZoomControls />
                </div>
              </div>

              {/* The Map itself */}
              <ComposableMap
                tabIndex="-1"
                projectionConfig={projectionConfig}
                width={mapWidth}
                height={height}
                style={this.mapStyle}
              >
                <ZoomableGroup
                  center={mapCenter}
                  zoom={mapZoom}
                  onMoveStart={this.onPageScrollOrResize}
                >
                  <Geographies geography={mapData}>
                    {({ geographies }) =>
                      geographies.map(
                        (geography) =>
                          geography.id !== 'ATA' && (
                            <a
                              onMouseOver={() => this.onCountryMouseEnter(geography.id)}
                              onMouseOut={() => this.onCountryMouseLeave(geography.id)}
                              onClick={() => this.onCountryClick(geography.id)}
                              data-area-id={geography.id}
                              key={geography.id}
                            >
                              <g tabIndex="-1">
                                {/* Each Geography is representation of country */}
                                {countryCodes.includes(geography.id) ? (
                                  <Geography
                                    geography={geography}
                                    data-tip=""
                                    style={
                                      geography.id === activeCountryCode
                                        ? this.activeCountryStyle
                                        : this.enabledCountryStyle
                                    }
                                  />
                                ) : (
                                  <Geography
                                    geography={geography}
                                    style={this.inactiveCountryStyle}
                                  />
                                )}
                              </g>
                            </a>
                          ),
                      )
                    }
                  </Geographies>

                  {/* Pins on countries where CW work */}
                  <g className="rsm-markers">
                    {mapData.map(
                      (country) =>
                        countryCodes.includes(country.id) && (
                          <a
                            data-marker-id={country.id}
                            onClick={() => this.onCountryClick(country.id)}
                            onMouseEnter={() => this.onCountryMouseEnter(country.id)}
                            onMouseLeave={() => this.onCountryMouseLeave(country.id)}
                            key={country.id}
                          >
                            <Marker style={this.markerStyle} coordinates={geoCentroid(country)}>
                              <g style={{ transform: 'scale(0.35)' }}>
                                <g tabIndex="-1" style={{ transform: 'translate(-11px, -25px)' }}>
                                  <path
                                    style={{ opacity: 0.2 }}
                                    d="M11,20.49c-6.08,0-11,1.57-11,3.5S11,30,11,30,22,26.21,22,24C22,22.06,17.08,20.49,11,20.49Z"
                                  />
                                  <path d="M11,0A11,11,0,0,0,0,11c0,6.5,11,19,11,19S22,18,22,11A11,11,0,0,0,11,0Z" />
                                  <path
                                    style={{ fill: '#ffffff' }}
                                    d="M11,14.66a4,4,0,1,1,4-4A4,4,0,0,1,11,14.66Z"
                                  />
                                </g>
                              </g>
                            </Marker>
                          </a>
                        ),
                    )}
                  </g>
                </ZoomableGroup>
              </ComposableMap>
            </>
          )}
        </div>
      </div>
    );
  }
}

BBCountryMap.propTypes = {
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      nextLink: PropTypes.shape({
        href: PropTypes.string,
        as: PropTypes.string,
      }).isRequired,
      linkLabel: PropTypes.string,
      name: PropTypes.string.isRequired,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
      description: PropTypes.string,
    }),
  ),
  height: PropTypes.number,
  projectionConfig: PropTypes.shape({
    scale: PropTypes.number,
    rotation: PropTypes.arrayOf(PropTypes.number),
  }),
};

BBCountryMap.defaultProps = {
  countries: [],
  height: 700,
  projectionConfig: {
    scale: 205,
    rotation: [-10, 0, 0],
  },
};

export default BBCountryMap;
